import React, { useEffect, useRef, useState } from 'react'
import { Button, Card, Form } from 'react-bootstrap'
import { useReactToPrint } from 'react-to-print'
import * as XLSX from 'xlsx'
import AdmissionLayout from './AdmissionLayout'
import { fetchReport, fetchYears } from './api/admissionApi'
import { classLabel, dateParts, nameParts } from './print/printFormat'
import { useLoading } from '../contexts/LoadingContext'
import './print/printDoc.css'
import '../print.css'

// ทะเบียนนักเรียนใหม่ (ทะเบียนใหญ่)
//
// ยกมาจาก reports/RegistrationReport.js ของระบบเดิม ซึ่งเป็นปลายทางของเมนู
// "ทะเบียนนักเรียนใหม่" — ตาราง 9 คอลัมน์ พิมพ์ทั้งเล่ม และบันทึกเป็น Excel
//
// ต่างจากเดิม: ตัวเลือกปีมาจากปีที่มีข้อมูลจริง (/admission/years) ของเดิมฮาร์ดโค้ด
// ไว้สามปีในหน้าเว็บ ต้องแก้โค้ดทุกปีเหมือนกับปีบนหัวเอกสารใบสมัคร

const thaiDate = (value) => {
  const p = dateParts(value)
  return p ? p.join(' ') : ''
}

const addressBlock = (row) => [
  row.st_current,
  row.st_currentSubDistrict && `ตำบล ${row.st_currentSubDistrict}`,
  row.st_currentDistrict && `อำเภอ ${row.st_currentDistrict}`,
  row.st_currentProvince && `จังหวัด ${row.st_currentProvince}`,
  row.st_zipcode && `รหัส ปณ. ${row.st_zipcode}`,
].filter(Boolean)

const Lines = ({ items }) => (
  <>
    {items.map((item, i) => (
      <React.Fragment key={i}>
        {i > 0 && <br />}
        {item}
      </React.Fragment>
    ))}
  </>
)

// ช่องที่มีป้ายกำกับหน้าแต่ละบรรทัด เหมือนของเดิม (ชื่อ / สกุล / 13หลัก / โทร / อาชีพ)
const Labelled = ({ rows }) => (
  <>
    {rows.map(([label, value], i) => (
      <React.Fragment key={label}>
        {i > 0 && <br />}
        <span className="fw-bold">{label}</span> {value || ''}
      </React.Fragment>
    ))}
  </>
)

export default function AdmissionRegisterBook() {
  const { setLoading } = useLoading()
  const [rows, setRows] = useState([])
  const [years, setYears] = useState([])
  const [year, setYear] = useState('')

  const printRef = useRef(null)
  const printBook = useReactToPrint({
    contentRef: printRef,
    documentTitle: `ทะเบียนนักเรียนใหม่-${year}`,
    // ตาราง 9 คอลัมน์ กว้างเกินกระดาษแนวตั้ง — ส่งผ่าน pageStyle แทนที่จะใส่ใน CSS รวม
    // ซึ่งจะไปบังคับใบสมัคร/ใบมอบตัวที่เป็นแนวตั้งด้วย
    pageStyle: '@page { size: A4 landscape; margin: 0.8cm; }',
  })

  // ทะเบียนใหญ่เป็นทะเบียนรายปีเสมอ ต้องมีปีที่เลือกอยู่ตลอด
  // เปิดหน้ามาให้ปีล่าสุดไว้ก่อน (fetchYears เรียงจากใหม่ไปเก่า)
  useEffect(() => {
    fetchYears()
      .then((list) => {
        setYears(list)
        setYear((current) => current || String(list[0] || ''))
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!year) return
    setLoading(true)
    fetchReport(year)
      .then(setRows)
      .finally(() => setLoading(false))
  }, [year, setLoading])

  const exportExcel = () => {
    const data = rows.map((r) => {
      const n = nameParts(r.st_name)
      return {
        เลขประจำตัวนักเรียน: String(r.st_idnumber || '').trim(),
        เลขประจำตัวประชาชน: r.st_idcard || '',
        'ชื่อ-สกุล': n.full,
        'ว.ด.ป. เกิด': thaiDate(r.st_birthdate),
        สมัครเรียนชั้น: classLabel(r.regis_academicClass),
      }
    })
    const sheet = XLSX.utils.json_to_sheet(data)
    const book = { Sheets: { p1: sheet }, SheetNames: ['p1'] }
    XLSX.writeFile(book, `ทะเบียนใหญ่นักเรียน-${year}.xlsx`)
  }

  const heading = `ทะเบียนใหญ่นักเรียน ${year}`

  return (
    <AdmissionLayout
      title="ทะเบียนใหญ่นักเรียน"
      breadcrumb={[{ label: 'ทะเบียนใหญ่นักเรียน' }]}
      actions={
        <>
          <Button variant="success" onClick={printBook} disabled={!rows.length}>
            <i className="ri-printer-line me-1" />ปริ้น
          </Button>
          <Button variant="primary" onClick={exportExcel} disabled={!rows.length}>
            <i className="ri-file-excel-2-line me-1" />บันทึกเป็น Excel
          </Button>
        </>
      }
    >
      <Card className="card-one mb-3">
        <Card.Body className="d-flex flex-wrap align-items-center gap-3">
          <div style={{ minWidth: 260 }}>
            <Form.Label className="fs-sm mb-1">ปีการศึกษา</Form.Label>
            <Form.Select size="sm" value={year} onChange={(e) => setYear(e.target.value)}>
              {years.map((y) => <option key={y} value={y}>ปีการศึกษา {y}</option>)}
            </Form.Select>
          </div>
          <div className="ms-auto fs-sm text-secondary">
            ทั้งหมด <strong>{rows.length.toLocaleString('th-TH')}</strong> คน
          </div>
        </Card.Body>
      </Card>

      <Card className="card-one">
        <Card.Body className="p-2">
          <div ref={printRef} className="adm-book pb-1 ps-3 pe-3">
            <div className="table-responsive">
              <table className="table table-bordered table-sm mb-0">
                <thead>
                  <tr style={{ border: 'none' }}>
                    <th colSpan={9} className="text-center fs-5 pt-3" style={{ border: 'none' }}>
                      {heading}
                    </th>
                  </tr>
                  <tr>
                    <th className="text-center" style={{ width: 80 }}>เลขประจำตัว</th>
                    <th className="text-center">ชื่อ นร.</th>
                    <th className="text-center" style={{ width: 110 }}>วดป</th>
                    <th className="text-center" style={{ width: 250 }}>ที่อยู่</th>
                    <th className="text-center">บิดา</th>
                    <th className="text-center">มารดา</th>
                    <th className="text-center" style={{ width: 60 }}>จบชั้น</th>
                    <th className="text-center" style={{ width: 60 }}>เข้าสามัญ</th>
                    <th className="text-center">ร.ร. เดิม/จังหวัด</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={9} className="text-center py-4">
                        {year ? 'ไม่มีข้อมูลในปีการศึกษานี้' : 'กำลังโหลด…'}
                      </td>
                    </tr>
                  )}
                  {rows.map((r) => {
                    const student = nameParts(r.st_name)
                    const father = nameParts(r.st_fatherName)
                    const mother = nameParts(r.st_motherName)
                    return (
                      <tr key={r.st_id}>
                        <td className="text-center">{String(r.st_idnumber || '').trim()}</td>
                        <td>
                          <Labelled rows={[
                            ['ชื่อ', student.prefixFirst],
                            ['สกุล', student.last],
                            ['13หลัก', r.st_idcard],
                            ['โทร', r.st_parentPhone || r.st_fatherPhone],
                          ]} />
                        </td>
                        <td className="text-center">{thaiDate(r.st_birthdate)}</td>
                        <td><Lines items={addressBlock(r)} /></td>
                        <td>
                          <Labelled rows={[
                            ['ชื่อ', father.prefixFirst],
                            ['สกุล', father.last],
                            ['13หลัก', r.st_fatherIdCard],
                            ['โทร', r.st_fatherPhone],
                            ['อาชีพ', r.st_fatherJob],
                          ]} />
                        </td>
                        <td>
                          <Labelled rows={[
                            ['ชื่อ', mother.prefixFirst],
                            ['สกุล', mother.last],
                            ['13หลัก', r.st_motherIdCard],
                            ['โทร', r.st_motherPhone],
                            ['อาชีพ', r.st_motherJob],
                          ]} />
                        </td>
                        <td className="text-center">{classLabel(r.st_prevAcademicClass)}</td>
                        <td className="text-center">{classLabel(r.regis_academicClass)}</td>
                        <td>
                          <Labelled rows={[
                            ['โรงเรียน', r.st_prevAcademicSchool],
                            ['ตำบล', r.st_prevAcademicSubDistrict],
                            ['อำเภอ', r.st_prevAcademicDistrict],
                            ['จังหวัด', r.st_prevAcademicProvince],
                          ]} />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </Card.Body>
      </Card>
    </AdmissionLayout>
  )
}
